import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { TextField } from "@mui/material";
import { NavigateBefore } from "@mui/icons-material";
import PublishIcon from "@mui/icons-material/Publish";

import AppDialog from "../../../../components/AppDialog/AppDialog";
import CustomButton from "../../../../components/buttons/CustomButton";
import LanguageTabs from "../../../../components/LanguageTab/LanguageTabs";
import { availableLanguages } from "../../../../i18n";
import { validateURL } from "../../../../utils/constants";
import {
  isValidHttpUrl,
  joinTruthyList,
  normalizeLangArray,
  normalizeTrimmedLangEntries,
  parseCommaSeparatedList,
} from "../../../../utils/formUtils";

import { useWorkerApiHelper } from "../../../../hooks/useWorkerApiHelper";
import { useGetDataServiceById } from "../../../../hooks/dataservice/useGetDataServiceById";

import "./CreateDataService.css";

const canonicalizePayload = (payload) => {
  const title = normalizeTrimmedLangEntries(payload.title || []);
  const description = normalizeTrimmedLangEntries(payload.description || []);

  const applicable_legislation = Array.isArray(payload.applicable_legislation)
    ? [...payload.applicable_legislation]
        .map((x) => String(x).trim())
        .filter(Boolean)
        .sort()
    : [];

  const out = {
    ...payload,
    title,
    description,
    documentation: String(payload.documentation || "").trim(),
    endpoint_url: String(payload.endpoint_url || "").trim(),
    endpoint_description: String(payload.endpoint_description || "").trim(),
    applicable_legislation,
  };

  if (Array.isArray(out.serves_dataset)) {
    out.serves_dataset = out.serves_dataset.filter(Boolean).sort();
  }

  return out;
};

const buildPayloadFromForm = (formState, servesDatasetState) => {
  const title = (formState.title || [])
    .map((x) => ({ language: x.language, value: String(x.value || "").trim() }))
    .filter((x) => x.value.length > 0);

  const description = (formState.description || [])
    .map((x) => ({ language: x.language, value: String(x.value || "").trim() }))
    .filter((x) => x.value.length > 0);

  const applicable_legislation = String(
    formState.applicable_legislation_text || "",
  );

  const payload = {
    title,
    description,
    documentation: String(formState.documentation || "").trim(),
    endpoint_url: String(formState.endpoint_url || "").trim(),
    endpoint_description: String(formState.endpoint_description || "").trim(),
    applicable_legislation: parseCommaSeparatedList(applicable_legislation),
  };

  const normalizedServes = Array.isArray(servesDatasetState)
    ? servesDatasetState.filter(Boolean)
    : [];

  if (normalizedServes.length > 0) {
    payload.serves_dataset = normalizedServes;
  }

  return payload;
};

const emptyForm = (baseLang) => ({
  title: [{ language: baseLang, value: "" }],
  description: [{ language: baseLang, value: "" }],
  documentation: "",
  endpoint_url: "",
  endpoint_description: "",
  applicable_legislation_text: "",
});

const normalizeServesDataset = (servesDataset) =>
  Array.isArray(servesDataset) ? servesDataset.filter(Boolean) : [];

export default function DataServiceForm({
  open,
  onClose,
  onSubmit,
  loading = false,
  t,
  i18n,
  initialDataService = null,
  servesDataset = [],
  includeIdentifiersInPayload = false,
  enableDirtyCheck = true,
}) {
  const [baseLang] = useState(() => i18n.language);

  const languages = availableLanguages;
  const editId =
    typeof initialDataService === "string" ||
    typeof initialDataService === "number"
      ? String(initialDataService)
      : initialDataService?.id
        ? String(initialDataService.id)
        : null;

  const mode = editId || initialDataService ? "edit" : "create";

  const { callApiWithWorker } = useWorkerApiHelper();

  const {
    data: fetchedDataService,
    isFetching: isFetchingDataService,
  } = useGetDataServiceById({
    callApiWithWorker,
    id: editId,
    enabled: open && Boolean(editId),
  });

  const effectiveInitial = useMemo(() => {
    if (mode !== "edit") return null;
    return fetchedDataService || null;
  }, [mode, fetchedDataService]);

  const [activeLang, setActiveLang] = useState(baseLang);
  const [form, setForm] = useState(() => emptyForm(baseLang));

  const initialCanonicalRef = useRef(null);
  const servesDatasetKey = useMemo(
    () => JSON.stringify(normalizeServesDataset(servesDataset)),
    [servesDataset],
  );
  const normalizedServesDataset = useMemo(
    () => JSON.parse(servesDatasetKey),
    [servesDatasetKey],
  );
  const servesDatasetRef = useRef(normalizedServesDataset);
  servesDatasetRef.current = normalizedServesDataset;

  const hydrate = useCallback(() => {
    if (mode === "create" || !effectiveInitial) {
      setActiveLang(baseLang);
      const next = emptyForm(baseLang);
      setForm(next);
      initialCanonicalRef.current = canonicalizePayload(
        buildPayloadFromForm(next, servesDatasetRef.current),
      );
      return;
    }

    const ds = effectiveInitial;

    const title = normalizeLangArray(ds.title, baseLang);
    const description = normalizeLangArray(ds.description, baseLang);

    const langsPresent = title.map((x) => x.language);
    const initialActive = langsPresent.includes(baseLang)
      ? baseLang
      : title[0]?.language || baseLang;

    const next = {
      title,
      description,
      documentation: ds.documentation || "",
      endpoint_url: ds.endpoint_url || "",
      endpoint_description: ds.endpoint_description || "",
      applicable_legislation_text: joinTruthyList(
        ds.applicable_legislation,
      ),
    };

    setActiveLang(initialActive);
    setForm(next);

    initialCanonicalRef.current = canonicalizePayload(
      buildPayloadFromForm(next, servesDatasetRef.current),
    );
  }, [mode, effectiveInitial, baseLang]);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && editId) {
      if (!fetchedDataService) return;
    }

    hydrate();
  }, [open, mode, editId, fetchedDataService, hydrate]);

  useEffect(() => {
    if (!open) return;

    setForm((prev) => {
      const hasTitle = prev.title.some((x) => x.language === activeLang);
      const hasDesc = prev.description.some((x) => x.language === activeLang);

      if (hasTitle && hasDesc) return prev;

      return {
        ...prev,
        title: hasTitle
          ? prev.title
          : [...prev.title, { language: activeLang, value: "" }],
        description: hasDesc
          ? prev.description
          : [...prev.description, { language: activeLang, value: "" }],
      };
    });
  }, [activeLang, open]);

  const getLangValue = useCallback((arr, language) => {
    return arr.find((x) => x.language === language)?.value ?? "";
  }, []);

  const setLangValue = useCallback(
    (key, language) => (e) => {
      const v = e.target.value;
      setForm((prev) => ({
        ...prev,
        [key]: prev[key].map((item) =>
          item.language === language ? { ...item, value: v } : item,
        ),
      }));
    },
    [],
  );

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const addLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code) return;

    setForm((prev) => {
      const hasTitle = prev.title.some((x) => x.language === code);
      const hasDesc = prev.description.some((x) => x.language === code);

      if (hasTitle && hasDesc) return prev;

      return {
        ...prev,
        title: hasTitle
          ? prev.title
          : [...prev.title, { language: code, value: "" }],
        description: hasDesc
          ? prev.description
          : [...prev.description, { language: code, value: "" }],
      };
    });

    if (code !== activeLang) setActiveLang(code);
  };

  const removeLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code) return;

    setForm((prev) => {
      const nextTitle = prev.title.filter((x) => x.language !== code);
      const nextDesc = prev.description.filter((x) => x.language !== code);

      if (nextTitle.length === 0 || nextDesc.length === 0) return prev;

      return { ...prev, title: nextTitle, description: nextDesc };
    });

    if (activeLang === code) {
      const next = form.title.map((x) => x.language).find((l) => l !== code);
      if (next) setActiveLang(next);
    }
  };

  const activeTitle = getLangValue(form.title, activeLang);
  const activeDescription = getLangValue(form.description, activeLang);

  const endpointOk = useMemo(
    () => isValidHttpUrl(form.endpoint_url),
    [form.endpoint_url],
  );

  const docsOk = useMemo(
    () => isValidHttpUrl(form.documentation),
    [form.documentation],
  );

  const applicable_legislation = useMemo(() => {
    return parseCommaSeparatedList(form.applicable_legislation_text);
  }, [form.applicable_legislation_text]);

  const legislationError = useMemo(() => {
    if (applicable_legislation.length === 0) return "";
    for (const entry of applicable_legislation) {
      const { valid, error } = validateURL(entry, t);
      if (!valid) return error || (t ? t("common.invalid_url") : "URL inválida");
    }
    return "";
  }, [applicable_legislation, t]);

  const hasAnyLangText = useMemo(() => {
    const anyTitle = form.title.some((x) => x.value.trim().length > 0);
    const anyDesc = form.description.some((x) => x.value.trim().length > 0);
    return anyTitle && anyDesc;
  }, [form.title, form.description]);

  const currentPayload = useMemo(
    () => buildPayloadFromForm(form, normalizedServesDataset),
    [form, normalizedServesDataset],
  );

  const busy = useMemo(() => {
    if (loading) return true;
    if (mode === "edit" && editId) return isFetchingDataService;
    return false;
  }, [loading, mode, editId, isFetchingDataService]);

  const canSubmit = useMemo(() => {
    const endpointRequiredOk =
      form.endpoint_url.trim().length > 0 && endpointOk;
    const docsRequiredOk = form.documentation.trim().length > 0 && docsOk;

    const endpointDescOk = form.endpoint_description.trim().length > 0;
    const legislationOk =
      applicable_legislation.length === 0 || legislationError === "";

    return (
      hasAnyLangText &&
      endpointRequiredOk &&
      docsRequiredOk &&
      endpointDescOk &&
      legislationOk &&
      !busy
    );
  }, [
    hasAnyLangText,
    form.endpoint_url,
    form.documentation,
    form.endpoint_description,
    endpointOk,
    docsOk,
    applicable_legislation.length,
    legislationError,
    busy,
  ]);

  const isDirty = useMemo(() => {
    if (mode !== "edit" || !enableDirtyCheck) return true;
    if (!initialCanonicalRef.current) return true;

    const now = canonicalizePayload(currentPayload);
    return JSON.stringify(now) !== JSON.stringify(initialCanonicalRef.current);
  }, [mode, enableDirtyCheck, currentPayload]);

  const handleSubmit = async () => {
    if (!canSubmit || busy) return;

    const payload = { ...currentPayload };

    if (mode === "edit" && includeIdentifiersInPayload && effectiveInitial) {
      if (effectiveInitial.id) payload.id = effectiveInitial.id;
      if (effectiveInitial.identifier)
        payload.identifier = effectiveInitial.identifier;
    }

    await onSubmit?.(payload, { mode, initialDataService: effectiveInitial });
  };

  const usedLangs = form.title.map((x) => x.language);
  const availableLangs = (languages || []).filter(
    (l) => !usedLangs.includes(l.code),
  );

  const dialogTitle =
    mode === "edit"
      ? t
        ? t("wizard.steps.dataset.dataservice.edit")
        : "Editar servicio de datos"
      : t
        ? t("wizard.steps.dataset.dataservice.create")
        : "Crear servicio de datos";

  const submitLabel =
    mode === "edit"
      ? t
        ? t("common.save")
        : "Guardar"
      : t
        ? t("common.create")
        : "Crear";

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth="md"
      disableBackdropClose={busy}
      disableEscapeClose={busy}
      actions={
        <>
          <CustomButton
            onClick={onClose}
            disabled={busy}
            startIcon={<NavigateBefore />}
          >
            {t ? t("common.cancel") : "Cancelar"}
          </CustomButton>

          <CustomButton
            onClick={handleSubmit}
            disabled={!canSubmit || busy || (mode === "edit" && !isDirty)}
            variant="contained"
            startIcon={<PublishIcon />}
          >
            {submitLabel}
          </CustomButton>
        </>
      }
    >
      <LanguageTabs
        usedLangs={usedLangs}
        activeLang={activeLang}
        onChangeActiveLang={(lang) => {
          const code = typeof lang === "string" ? lang : lang?.code;
          if (code && code !== activeLang) setActiveLang(code);
        }}
        onAddLang={addLanguage}
        onRemoveLang={removeLanguage}
        languages={availableLangs}
        addLabel={t ? t("common.addLanguage") : "Añadir idioma"}
        disabled={busy}
      />
      <div className="cds-grid">
        <div className="cds-left">
          <TextField
            label={
              t
                ? t("wizard.steps.dataset.dataservice.name_label")
                : "Nombre del servicio de datos"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.dataservice.name_label_placeholder")
                : "Escribe el nombre del servicio de datos"
            }
            value={activeTitle}
            onChange={setLangValue("title", activeLang)}
            fullWidth
            disabled={busy}
            required
          />

          <TextField
            label={
              t ? t("wizard.steps.dataset.dataservice.url") : "Endpoint URL"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.dataservice.url_placeholder")
                : "Escribe url de acceso al servicio de datos"
            }
            value={form.endpoint_url}
            onChange={setField("endpoint_url")}
            fullWidth
            disabled={busy}
            required
            error={form.endpoint_url.trim().length > 0 && !endpointOk}
            helperText={
              form.endpoint_url && !endpointOk
                ? t
                  ? t("common.invalid_url")
                  : "URL inválida"
                : " "
            }
          />

          <TextField
            label={
              t
                ? t("wizard.steps.dataset.dataservice.documentation")
                : "Documentación"
            }
            placeholder={
              t
                ? t(
                    "wizard.steps.dataset.dataservice.documentation_placeholder",
                  )
                : "Escribe url de documentación del servicio de datos"
            }
            value={form.documentation}
            onChange={setField("documentation")}
            fullWidth
            disabled={busy}
            required
            error={form.documentation.trim().length > 0 && !docsOk}
            helperText={
              form.documentation.trim().length === 0
                ? " "
                : docsOk
                  ? " "
                  : t
                    ? t("common.invalid_url")
                    : "URL inválida"
            }
          />
          <TextField
            label={
              t
                ? t("wizard.steps.dataset.dataservice.endpoint_description")
                : "Descripción del endpoint"
            }
            placeholder={
              t
                ? t(
                    "wizard.steps.dataset.dataservice.endpoint_description_placeholder",
                  )
                : "Describe el endpoint del servicio de datos"
            }
            value={form.endpoint_description}
            onChange={setField("endpoint_description")}
            fullWidth
            disabled={busy}
            required
          />
        </div>

        <div className="cds-right">
          <TextField
            label={
              t
                ? t("wizard.steps.dataset.dataservice.description")
                : "Descripción"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.dataservice.description_placeholder")
                : "Escribe la descripción del servicio de datos"
            }
            value={activeDescription}
            onChange={setLangValue("description", activeLang)}
            fullWidth
            multiline
            minRows={4}
            disabled={busy}
            required
          />

          <TextField
            label={
              t
                ? t("wizard.steps.dataset.dataservice.legislation")
                : "Legislación aplicable"
            }
            placeholder={
              t
                ? t("wizard.steps.dataset.dataservice.legislation_placeholder")
                : "Escribe la legislación que aplica al servicio de datos"
            }
            value={form.applicable_legislation_text}
            onChange={setField("applicable_legislation_text")}
            fullWidth
            multiline
            minRows={4}
            disabled={busy}
            error={form.applicable_legislation_text.trim().length > 0 && legislationError !== ""}
            helperText={
              form.applicable_legislation_text.trim().length === 0
                ? " "
                : legislationError || " "
            }
          />
        </div>
      </div>
    </AppDialog>
  );
}
